/**
 * Drop-in replacement for Mantine's `TextInput` / `Textarea` that supports
 * inserting template variables via:
 *   - a small "{}" toolbar button (popover picker), and
 *   - typing `@` to open the same picker as autocomplete.
 *
 * The wrapper deliberately mirrors the props used by the Document Builder
 * inputs (variant, size, styles, placeholder, value, onChange) so we can
 * swap usages 1:1 without reflowing layout.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Box,
  Group,
  Popover,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
  type TextInputProps,
  type TextareaProps,
} from '@mantine/core';

import {
  buildVariableToken,
  getRegisteredVariables,
  type VariableDefinition,
} from '../registry';
import { VariablePickerPopover } from './VariablePickerPopover';

interface CommonVariableInputProps {
  /** Current text value. */
  value: string;
  /** Update the text value. Receives the entire next string. */
  onChange: (next: string) => void;
  /**
   * When true, edits stay local while typing and commit to `onChange` on blur
   * (and Enter for single-line inputs). Use for heavy parent re-renders.
   */
  deferredCommit?: boolean;
  /** Set of variable ids the picker should advertise. Defaults to the full registry. */
  filterIds?: Set<string>;
  /**
   * When true, the picker trigger button is hidden — variables can only be
   * inserted via `@`-autocomplete. Useful for very compact surfaces.
   */
  hidePickerTrigger?: boolean;
}

// ---------------------------------------------------------------------------
// VariableTextInput
// ---------------------------------------------------------------------------

export type VariableTextInputProps = CommonVariableInputProps &
  Omit<TextInputProps, 'value' | 'onChange' | 'rightSection'>;

export const VariableTextInput = forwardRef<HTMLInputElement, VariableTextInputProps>(
  function VariableTextInput(
    { value, onChange, deferredCommit = false, filterIds, hidePickerTrigger, onBlur, onKeyDown, ...inputProps },
    ref,
  ): JSX.Element {
    const innerRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

    const [draft, setDraft] = useState(value);
    useEffect(() => {
      setDraft(value);
    }, [value]);

    const effectiveValue = deferredCommit ? draft : value;

    const emitChange = useCallback(
      (next: string) => {
        if (deferredCommit) {
          setDraft(next);
        } else {
          onChange(next);
        }
      },
      [deferredCommit, onChange],
    );

    const commit = useCallback(() => {
      if (deferredCommit && draft !== value) {
        onChange(draft);
      }
    }, [deferredCommit, draft, onChange, value]);

    const autocomplete = useAtAutocomplete<HTMLInputElement>({
      value: effectiveValue,
      onChange: emitChange,
      filterIds,
      inputRef: innerRef,
    });

    return (
      <Box style={{ position: 'relative', width: '100%', minWidth: 0 }}>
        <TextInput
          {...inputProps}
          ref={innerRef}
          value={effectiveValue}
          onChange={(e) => {
            emitChange(e.currentTarget.value);
            autocomplete.handleChange(e.currentTarget);
          }}
          onBlur={(e) => {
            commit();
            onBlur?.(e);
          }}
          onKeyDown={(e) => {
            autocomplete.handleKeyDown(e);
            if (deferredCommit && e.key === 'Enter') {
              commit();
            }
            onKeyDown?.(e);
          }}
          rightSection={
            hidePickerTrigger ? undefined : (
              <VariablePickerPopover
                filterIds={filterIds}
                onPick={(token) => insertAtCaret(innerRef.current, effectiveValue, token, emitChange)}
              />
            )
          }
        />
        {autocomplete.menu}
      </Box>
    );
  },
);

// ---------------------------------------------------------------------------
// VariableTextarea
// ---------------------------------------------------------------------------

export type VariableTextareaProps = CommonVariableInputProps &
  Omit<TextareaProps, 'value' | 'onChange' | 'rightSection'> & {
    /** Optional toolbar position for the picker trigger. Defaults to `'inline'`. */
    pickerPosition?: 'inline' | 'header';
    /** Optional label shown above the toolbar in `'header'` mode. */
    pickerHeader?: string;
  };

export const VariableTextarea = forwardRef<HTMLTextAreaElement, VariableTextareaProps>(
  function VariableTextarea(
    {
      value,
      onChange,
      deferredCommit = false,
      filterIds,
      hidePickerTrigger,
      pickerPosition = 'inline',
      pickerHeader,
      onBlur,
      onKeyDown,
      ...textareaProps
    },
    ref,
  ): JSX.Element {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    const [draft, setDraft] = useState(value);
    useEffect(() => {
      setDraft(value);
    }, [value]);

    const effectiveValue = deferredCommit ? draft : value;

    const emitChange = useCallback(
      (next: string) => {
        if (deferredCommit) {
          setDraft(next);
        } else {
          onChange(next);
        }
      },
      [deferredCommit, onChange],
    );

    const commit = useCallback(() => {
      if (deferredCommit && draft !== value) {
        onChange(draft);
      }
    }, [deferredCommit, draft, onChange, value]);

    const autocomplete = useAtAutocomplete<HTMLTextAreaElement>({
      value: effectiveValue,
      onChange: emitChange,
      filterIds,
      inputRef: innerRef,
    });

    return (
      <Stack gap={2} style={{ position: 'relative', width: '100%', minWidth: 0 }}>
        {pickerPosition === 'header' && !hidePickerTrigger && (
          <Group justify="space-between" align="center">
            {pickerHeader && (
              <Text size="xs" c="gray.6">
                {pickerHeader}
              </Text>
            )}
            <VariablePickerPopover
              filterIds={filterIds}
              onPick={(token) =>
                insertAtCaret(innerRef.current, effectiveValue, token, emitChange)
              }
            />
          </Group>
        )}
        <Box style={{ position: 'relative', width: '100%', minWidth: 0 }}>
          <Textarea
            {...textareaProps}
            ref={innerRef}
            value={effectiveValue}
            onChange={(e) => {
              emitChange(e.currentTarget.value);
              autocomplete.handleChange(e.currentTarget);
            }}
            onBlur={(e) => {
              commit();
              onBlur?.(e);
            }}
            onKeyDown={(e) => {
              autocomplete.handleKeyDown(e);
              onKeyDown?.(e);
            }}
            rightSection={
              pickerPosition === 'inline' && !hidePickerTrigger ? (
                <Box style={{ alignSelf: 'flex-start', paddingTop: 6, paddingRight: 2 }}>
                  <VariablePickerPopover
                    filterIds={filterIds}
                    onPick={(token) =>
                      insertAtCaret(innerRef.current, effectiveValue, token, emitChange)
                    }
                  />
                </Box>
              ) : undefined
            }
            rightSectionPointerEvents="all"
          />
          {autocomplete.menu}
        </Box>
      </Stack>
    );
  },
);

// ---------------------------------------------------------------------------
// Internals: caret-aware insertion + `@` autocomplete
// ---------------------------------------------------------------------------

function insertAtCaret(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  current: string,
  token: string,
  onChange: (next: string) => void,
): void {
  if (!el) {
    onChange(current + token);
    return;
  }
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  const next = current.slice(0, start) + token + current.slice(end);
  onChange(next);
  // Restore caret to just after the inserted token on next tick so React has
  // committed the new value first.
  requestAnimationFrame(() => {
    if (!el) return;
    const caret = start + token.length;
    el.focus();
    el.setSelectionRange(caret, caret);
  });
}

interface UseAtAutocompleteArgs<T extends HTMLInputElement | HTMLTextAreaElement> {
  value: string;
  onChange: (next: string) => void;
  filterIds?: Set<string>;
  inputRef: React.MutableRefObject<T | null>;
}

interface UseAtAutocompleteResult {
  handleChange: (target: HTMLInputElement | HTMLTextAreaElement) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  menu: JSX.Element | null;
}

/**
 * Lightweight `@`-autocomplete: when the user types `@` followed by alpha-
 * numerics, we open a small inline menu of matching variables. Pressing
 * Enter or Tab inserts the token; Escape closes. We deliberately reuse the
 * popover styling but render it inline so it follows the caret in textareas
 * without depending on a positioning library.
 */
function useAtAutocomplete<T extends HTMLInputElement | HTMLTextAreaElement>(
  args: UseAtAutocompleteArgs<T>,
): UseAtAutocompleteResult {
  const { value, onChange, filterIds, inputRef } = args;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const triggerStartRef = useRef<number | null>(null);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlight(0);
    triggerStartRef.current = null;
  }, []);

  const matches = (() => {
    if (!open) return [];
    const all = getRegisteredVariables();
    const filtered = filterIds ? all.filter((v) => filterIds.has(v.id)) : all;
    const q = query.toLowerCase();
    return filtered
      .filter((v) => !q || v.id.toLowerCase().includes(q) || v.label.toLowerCase().includes(q))
      .slice(0, 8);
  })();

  const insertSelected = useCallback(
    (def: VariableDefinition) => {
      const start = triggerStartRef.current;
      if (start === null) return;
      const caret = inputRef.current?.selectionStart ?? value.length;
      const before = value.slice(0, start);
      const after = value.slice(caret);
      const token = buildVariableToken(def.id);
      const next = before + token + after;
      onChange(next);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        const newCaret = start + token.length;
        el.focus();
        el.setSelectionRange(newCaret, newCaret);
      });
      closeMenu();
    },
    [closeMenu, inputRef, onChange, value],
  );

  const handleChange = useCallback(
    (target: HTMLInputElement | HTMLTextAreaElement) => {
      const caret = target.selectionStart ?? target.value.length;
      const text = target.value;
      // Walk back from the caret to find an unclosed `@` token.
      let i = caret - 1;
      while (i >= 0) {
        const ch = text[i];
        if (ch === '@') {
          // Make sure `@` is at the start or preceded by whitespace / punctuation.
          const prev = i === 0 ? ' ' : text[i - 1];
          if (/[\s({\[,;:]/.test(prev) || i === 0) {
            triggerStartRef.current = i;
            setQuery(text.slice(i + 1, caret));
            setOpen(true);
            setHighlight(0);
            return;
          }
          break;
        }
        if (/[^\w.-]/.test(ch)) break;
        i -= 1;
      }
      closeMenu();
    },
    [closeMenu],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, matches.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && matches[highlight]) {
        e.preventDefault();
        insertSelected(matches[highlight]);
      }
    },
    [closeMenu, highlight, insertSelected, matches, open],
  );

  const menu =
    open && matches.length > 0 ? (
      <Popover
        opened={open}
        onChange={(next) => {
          if (!next) closeMenu();
        }}
        closeOnClickOutside
        position="bottom-start"
        withArrow={false}
        offset={4}
        shadow="md"
      >
        <Popover.Target>
          {/* Anchor the inline menu to the bottom-left of the input via a zero-height div. */}
          <Box
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: 1,
              height: 1,
              pointerEvents: 'none',
            }}
          />
        </Popover.Target>
        <Popover.Dropdown p={4}>
          <ScrollArea.Autosize mah={220} w={280}>
            <Stack gap={2}>
              {matches.map((def, idx) => (
                <UnstyledButton
                  key={def.id}
                  onClick={() => insertSelected(def)}
                  p="xs"
                  style={{
                    borderRadius: 4,
                    backgroundColor:
                      idx === highlight ? 'var(--mantine-color-gold-0)' : 'transparent',
                  }}
                  onMouseEnter={() => setHighlight(idx)}
                >
                  <Stack gap={0}>
                    <Text size="xs" fw={600}>
                      {def.label}
                    </Text>
                    <Text size="xs" c="dimmed" ff="monospace">
                      {buildVariableToken(def.id)}
                    </Text>
                  </Stack>
                </UnstyledButton>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>
    ) : null;

  return { handleChange, handleKeyDown, menu };
}
